import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../Configurations/AuthContext";
import { showMenu } from "./CompanyMenu";
import { isPlanExpired } from "./dateUtils";
import DefaultData from "../../CompanyData/DefaultData";

const BG = DefaultData.THEMECOLOR1;   // #244892  header background
const FG = DefaultData.THEMECOLOR3;   // #ffffff  nav text
const ACTIVE_BG = DefaultData.THEMECOLOR3; // #ffffff  active pill bg
const ACTIVE_FG = DefaultData.THEMECOLOR1; // #244892  active pill text

const baseStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0.5rem 1rem",
  borderRadius: "0.4rem",
  textDecoration: "none",
  fontWeight: "500",
  fontSize: "0.875rem",
  whiteSpace: "nowrap",
  transition: "background-color 0.2s, color 0.2s",
};

// ─── NavLink ──────────────────────────────────────────────────────────────────
const NavLink = ({ to, icon, label, active }) => (
  <Link
    to={to}
    style={{
      ...baseStyle,
      color: active ? ACTIVE_FG : FG,
      backgroundColor: active ? ACTIVE_BG : "transparent",
    }}
  >
    <i className={icon} />
    <span>{label}</span>
  </Link>
);

// ─── DropdownItem ─────────────────────────────────────────────────────────────
const DropdownItem = ({ to, icon, label, active }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Link
      to={to}
      style={{
        ...baseStyle,
        color: active ? "#ffffff" : "#1e3a5f",
        backgroundColor: active ? BG : hovered ? "#eef2ff" : "transparent",
        padding: "0.45rem 0.8rem",
        fontSize: "0.855rem",
        borderRadius: "0.35rem",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <i className={icon} />
      <span>{label}</span>
    </Link>
  );
};

// ─── DropdownSectionLabel ─────────────────────────────────────────────────────
const DropdownSectionLabel = ({ text }) => (
  <div
    style={{
      padding: "0.4rem 0.8rem 0.15rem",
      fontSize: "0.68rem",
      color: "#94a3b8",
      fontWeight: "700",
      letterSpacing: "0.07em",
      textTransform: "uppercase",
    }}
  >
    {text}
  </div>
);

// ─── DropdownComingSoon ───────────────────────────────────────────────────────
const DropdownComingSoon = ({ icon, label }) => (
  <div
    style={{
      ...baseStyle,
      color: "#94a3b8",
      cursor: "not-allowed",
      padding: "0.45rem 0.8rem",
      fontSize: "0.855rem",
      borderRadius: "0.35rem",
      justifyContent: "space-between",
    }}
  >
    <span style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
      <i className={icon} />
      <span>{label}</span>
    </span>
    <span
      style={{
        fontSize: "0.6rem",
        fontWeight: "700",
        padding: "0.1rem 0.35rem",
        borderRadius: "1rem",
        backgroundColor: "#e2e8f0",
        color: "#64748b",
        letterSpacing: "0.05em",
      }}
    >
      SOON
    </span>
  </div>
);

// ─── NavDropdown ──────────────────────────────────────────────────────────────
const NavDropdown = ({ icon, label, active, children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  const clearClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(closeTimer.current);
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{ position: "relative" }}
      onMouseEnter={() => { clearClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          ...baseStyle,
          color: active || open ? ACTIVE_FG : FG,
          backgroundColor: active || open ? ACTIVE_BG : "transparent",
          border: "none",
          cursor: "pointer",
          outline: "none",
        }}
      >
        <i className={icon} />
        <span>{label}</span>
        <i
          className="ri-arrow-down-s-line"
          style={{
            fontSize: "1rem",
            marginLeft: "0.1rem",
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {open && (
        <div
          onMouseEnter={clearClose}
          onMouseLeave={scheduleClose}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            minWidth: "200px",
            backgroundColor: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 8px 24px rgba(36,72,146,0.18)",
            padding: "0.35rem",
            zIndex: 1100,
            border: "1px solid #e2e8f0",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Standard nav (non-special company types) ─────────────────────────────────
const StandardNav = ({ is, anyOf, user }) => {
  const isStaff = user?.role === "STAFF";

  return (
    <>
      {showMenu("dashboard") && (
        <NavLink to="/dashboard" icon="ri-home-4-line" label="" active={is("dashboard")} />
      )}

      {showMenu("marketing") && (
        <NavDropdown
          icon="ri-message-3-line"
          label="Leads"
          active={anyOf("leaddashboard", "leads", "leadcalendar", "leadreports", "marketing", "promotion", "customers", "contacts", "socialmediasettings")}
        >
          <DropdownItem to="/leaddashboard" icon="ri-dashboard-line" label="Dashboard" active={is("leaddashboard")} />
          <DropdownItem to="/leads" icon="ri-list-check-2" label="All Leads" active={is("leads")} />
          <DropdownItem to="/leadcalendar" icon="ri-calendar-line" label="Calendar" active={is("leadcalendar")} />

          {!isStaff && (
            <DropdownItem to="/lead/staffreports" icon="ri-file-list-3-line" label="Reports" active={is("leadreports")} />
          )}
          <DropdownItem to="/marketing" icon="ri-megaphone-line" label="Marketing" active={is("marketing")} />
          <DropdownItem to="/promotion" icon="ri-gift-line" label="Promotions" active={is("promotion")} />
          <DropdownItem to="/customers" icon="ri-group-line" label="Customers" active={is("customers")} />
          <DropdownItem to="/contacts" icon="ri-contacts-line" label="Contacts" active={is("contacts")} />

          {!isStaff && (
            <DropdownItem to="/socialmediasettings" icon="ri-share-circle-line" label="Social Media" active={is("socialmediasettings")} />
          )}
        </NavDropdown>
      )}

      <NavDropdown icon="ri-flight-takeoff-line" label="Flights" active={false}>
        <DropdownItem to="/flight/search" icon="ri-search-line" label="Search" active={is("flightsearch")} />
        <DropdownItem to="/flight/compare" icon="ri-scales-3-line" label="Compare" active={is("flightcompare")} />
        <DropdownItem to="/flight/bookings" icon="ri-calendar-check-line" label="Bookings" active={is("flightbookings")} />
        <DropdownItem to="/flight/report" icon="ri-file-list-3-line" label="Report" active={is("flightreport")} />

        {!isStaff && (
          <DropdownItem to="/flight/gstreport" icon="ri-receipt-line" label="GST Report" active={is("flightgstreport")} />
        )}

        {!isStaff && (
          <DropdownItem to="/flight/supplier-payable-report" icon="ri-money-dollar-circle-line" label="Supplier Payable Report" active={is("flightsupplierpayableReport")} />
        )}
        <DropdownItem to="/flight/voucher-generator" icon="ri-file-paper-2-line" label="Manual Voucher" active={is("flightmanualvoucher")} />
      </NavDropdown>

      <NavDropdown icon="ri-building-line" label="Hotels" active={false}>
        <DropdownItem to="/hotel/search" icon="ri-search-line" label="Search" active={is("hotelsearch")} />
        <DropdownItem to="/hotel/bookings" icon="ri-calendar-check-line" label="Bookings" active={is("hotelbookings")} />
        <DropdownItem to="/hotel/report" icon="ri-file-list-3-line" label="Report" active={is("hotelreport")} />

        {!isStaff && (
          <DropdownItem to="/hotel/gstreport" icon="ri-receipt-line" label="GST Report" active={is("hotelgstreport")} />
        )}

        {!isStaff && (
          <DropdownItem to="/hotel/supplier-payable-report" icon="ri-money-dollar-circle-line" label="Supplier Payable Report" active={is("hotelsupplierpayablereport")} />
        )}
        <DropdownItem to="/hotel/voucher-generator" icon="ri-file-paper-2-line" label="Manual Voucher" active={is("hotelmanualvoucher")} />
        <DropdownItem to="/properties" icon="ri-price-tag-3-line" label="Direct Deals" active={is("hoteldirectdeals")} />
      </NavDropdown>

      {showMenu("buses") && (
        <NavDropdown
          icon="ri-bus-line"
          label="Buses"
          active={anyOf("bussearch", "busbookings", "busreport", "busgstreport", "bussupplierpayablereport", "bUSfOrm")}
        >
          <DropdownItem to="/bus/search" icon="ri-search-line" label="Search" active={is("bussearch")} />
          <DropdownItem to="/bus/bookings" icon="ri-calendar-check-line" label="Bookings" active={is("busbookings")} />
          <DropdownItem to="/bus/report" icon="ri-file-list-3-line" label="Bus Report" active={is("busreport")} />

          {!isStaff && (
            <DropdownItem to="/bus/gstreport" icon="ri-receipt-line" label="Gst Report" active={is("busgstreport")} />
          )}

          {!isStaff && (
            <DropdownItem to="/bus/supplier-payable-report" icon="ri-money-dollar-circle-line" label="Supplier Payable Report" active={is("bussupplierpayablereport")} />
          )}
          <DropdownItem to="/bus/form" icon="ri-bus-line" label="Bus Rentals" active={is("bUSfOrm")} />

        </NavDropdown>
      )}

      {showMenu("sightseeing") && (
        <NavDropdown
          icon="ri-map-pin-line"
          label="Sightseeing"
          active={anyOf("sightseeingsearch", "sightseeingbookings", "sightseeingdeals")}
        >
          <DropdownItem to="/sightseeing/search" icon="ri-search-line" label="Search" active={is("sightseeingsearch")} />
          <DropdownItem to="/sightseeing/bookings" icon="ri-file-list-3-line" label="Bookings" active={is("sightseeingbookings")} />
          <DropdownItem to="/sightseeing/deals" icon="ri-shopping-bag-line" label="SightSeeing Deals" active={is("sightseeingdeals")} />
        </NavDropdown>
      )}

      {/* {showMenu("packages") && (
      <NavDropdown
        icon="ri-luggage-deposit-line"
        label="Packages"
        active={anyOf("HOLIDAY PACKAGE", "PACKAGES", "AI PACKAGES", "DMCPACKAGESDEALS", "WEBSITEPACKAGES")}
      >
        <DropdownItem to="/package" icon="ri-add-circle-line" label="Create Package" active={is("HOLIDAY PACKAGE")} />
        <DropdownItem to="/packageslist" icon="ri-suitcase-line" label="Packages" active={is("PACKAGES")} />
        <DropdownItem to="/ai-packageslist" icon="ri-robot-line" label="AI Packages" active={is("AI PACKAGES")} />
        <DropdownItem to="/dmcpackagesdeals" icon="ri-map-2-line" label="DMC Deals" active={is("DMCPACKAGESDEALS")} />
        <DropdownItem to="/websitepackages" icon="ri-global-line" label="Website Packages" active={is("WEBSITEPACKAGES")} />
      </NavDropdown>
    )} */}
      {showMenu("packages") && (
        <NavDropdown
          icon="ri-luggage-deposit-line"
          label="Packages"
          active={anyOf("createpackage", "mypackages", "masterpackages", "dmcdeals", "customerpackages", "enquirypackages")}
        >
          <DropdownItem to="/createpackage" icon="ri-add-circle-line" label="Create Package" active={is("createpackage")} />
          <DropdownItem to="/companypackages" icon="ri-robot-line" label="My Packages " active={is("mypackages")} />
          {/* <DropdownItem to="/masterpackages" icon="ri-suitcase-line" label="Global Packages" active={is("masterpackages")} /> */}
          <DropdownItem to="/zotraipackages" icon="ri-map-2-line" label="Zotrai Packages" active={is("zotraipackages")} />
          <DropdownItem to="/customerpackages" icon="ri-user-3-line" label="Customer Packages" active={is("customerpackages")} />
          <DropdownItem to="/package/enquirylist" icon="ri-question-line" label="Enquiry Packages" active={is("enquirypackages")} />
        </NavDropdown>
      )}

      {showMenu("visas") && (
        <NavDropdown
          icon="ri-passport-line"
          label="Visas"
          active={anyOf("visas", "requests", "visareports", "visagstreport", "createb2cvisaprices")}
        >
          <DropdownItem to="/visas" icon="ri-passport-line" label="Search" active={is("visas")} />
          <DropdownItem to="/requests" icon="ri-file-list-3-line" label="Requests" active={is("requests")} />
          <DropdownItem to="/visa/report" icon="ri-bar-chart-box-line" label="Visa Reports" active={is("visareports")} />

          {!isStaff && (
            <DropdownItem to="/visa/gstreport" icon="ri-receipt-line" label="Visa Gst Report" active={is("visagstreport")} />
          )}
          <DropdownItem to="/visaappointments" icon="ri-calendar-check-line" label="Visa Appointments" active={is("visaappointments")} />
          <DropdownItem to="/create/visaprices" icon="ri-money-dollar-circle-line" label="Create B2C Visa Prices" active={is("createb2cvisaprices")} />
        </NavDropdown>
      )}
      {showMenu("accounts") && (
        <NavDropdown
          icon="ri-money-dollar-circle-line"
          label="Accounts"
          active={anyOf("accountsdashboard", "salelist", "purchaselist", "expenselist", "cheques", "profitlossdetailedrepor", "supplierledger", "agentledger", "companybankaccounts")}
        >
          <DropdownItem to="/accounts-dashboard" icon="ri-dashboard-line" label="Dashboard" active={is("accountsdashboard")} />
          <DropdownItem to="/salelist" icon="ri-shopping-cart-line" label="Sales" active={is("salelist")} />
          <DropdownItem to="/purchaselist" icon="ri-shopping-bag-line" label="Purchases" active={is("purchaselist")} />
          <DropdownItem to="/cheques" icon="ri-bank-card-line" label="Cheques" active={is("cheques")} />
          <DropdownItem to="/profit-loss-detailedreport" icon="ri-line-chart-line" label="Profit & Loss" active={is("profitlossdetailedrepor")} />
          <DropdownItem to="/supplier-ledger" icon="ri-store-2-line" label="Supplier Ledger" active={is("supplierledger")} />
          <DropdownItem to="/agent-ledger" icon="ri-user-star-line" label="Agent Ledger" active={is("agentledger")} />
          <DropdownItem to="/companybankaccounts" icon="ri-bank-line" label="Bank Accounts" active={is("companybankaccounts")} />
          <DropdownItem to="/creditnotelist" icon="ri-file-paper-2-line" label="Credit Notes" active={is("creditnotelist")} />
          <DropdownItem to="/debitnotelist" icon="ri-file-paper-2-line" label="Debit Notes" active={is("debitnotelist")} />
          <DropdownItem to="/journalentry" icon="ri-booklet-line" label="Journal Entries" active={is("journalentry")} />
          <DropdownItem to="/accountbook" icon="ri-book-2-line" label="Account Book" active={is("accountbook")} />
          <DropdownItem to="/statutorygst-report" icon="ri-receipt-line" label="Statutory GST Report" active={is("statutorygstreport")} />
          <DropdownItem to="/trialbalance" icon="ri-file-list-3-line" label="Trial Balance" active={is("trialbalance")} />

        </NavDropdown>
      )}

      <NavDropdown
        icon="ri-shield-line"
        label="Insurance"
        active={anyOf("insurance", "insurancelist")}
      >
        <DropdownItem to="/insurance" icon="ri-shield-line" label="Insurance" active={is("insurance")} />
        <DropdownItem to="/insurance/bookings" icon="ri-list-check-2" label="Insurance Bookings" active={is("insurancebookings")} />

      </NavDropdown>
      {/* <NavDropdown
        icon="ri-car-line"
        label="Cars"
        active={anyOf("transfer", "transferbookings")}
      >
        <DropdownItem to="/transfer/search" icon="ri-search-line" label="Search Cars" active={is("transfer")} />
        <DropdownItem to="/transfer/bookings" icon="ri-list-check-2" label="Car Bookings" active={is("transferbookings")} />
      </NavDropdown> */}

      {/* 
    <NavDropdown icon="ri-more-2-fill" label="More" active={false}>
      <DropdownSectionLabel text="Coming Soon" />
      <DropdownComingSoon icon="ri-passport-line" label="Visa" />
      <DropdownComingSoon icon="ri-car-line" label="Cars" />
    </NavDropdown> */}
    </>
  )
};

// ─── Special-type navs ────────────────────────────────────────────────────────
const HotelNav = ({ is }) => (
  <>
    <NavLink to="/hoteldashboard" icon="ri-home-4-line" label="Dashboard" active={is("HOTELDASHBOARD")} />
    <NavLink to="/myproperties" icon="ri-building-line" label="My Properties" active={is("HOTELPROPERTIES")} />
    <NavLink to="/hotels/search" icon="ri-hotel-line" label="My Hotels" active={is("MYHOTELS")} />
    <NavLink to="/leads" icon="ri-message-3-line" label="Leads" active={is("leads")} />
    <NavLink to="/accounts-dashboard" icon="ri-money-dollar-circle-line" label="Accounts" active={is("accounts")} />
  </>
);

const DmcNav = ({ is, anyOf }) => (
  <>
    <NavLink to="/holidaydashboard" icon="ri-home-4-line" label="Dashboard" active={is("HOLIDAYDASHBOARD")} />
    {showMenu("marketing") && (
      <NavDropdown
        icon="ri-message-3-line"
        label="Leads"
        active={anyOf("leaddashboard", "leads", "leadcalendar", "leadreports", "marketing", "promotion", "customers", "contacts", "socialmediasettings")}
      >
        <DropdownItem to="/leaddashboard" icon="ri-dashboard-line" label="Dashboard" active={is("leaddashboard")} />
        <DropdownItem to="/leads" icon="ri-list-check-2" label="All Leads" active={is("leads")} />
        <DropdownItem to="/leadcalendar" icon="ri-calendar-line" label="Calendar" active={is("leadcalendar")} />
        <DropdownItem to="/lead/staffreports" icon="ri-file-list-3-line" label="Reports" active={is("leadreports")} />
        <DropdownItem to="/marketing" icon="ri-megaphone-line" label="Marketing" active={is("marketing")} />
        <DropdownItem to="/promotion" icon="ri-gift-line" label="Promotions" active={is("promotion")} />
        <DropdownItem to="/customers" icon="ri-group-line" label="Customers" active={is("customers")} />
        <DropdownItem to="/contacts" icon="ri-contacts-line" label="Contacts" active={is("contacts")} />
        <DropdownItem to="/socialmediasettings" icon="ri-share-circle-line" label="Social Media" active={is("socialmediasettings")} />
      </NavDropdown>
    )}
  <NavDropdown
      icon="ri-search-line"
      label="Search"
      active={anyOf("properties", "activities", "createvisaprices","visarequests")}
    >
      <DropdownItem to="/myproperties" icon="ri-building-line" label="Properties" active={is("properties")} />
      <DropdownItem to="/activities" icon="ri-building-line" label="Activities" active={is("activities")} />
      <DropdownItem to="/create/visa/prices" icon="ri-money-dollar-circle-line" label="Create Visa Prices" active={is("createvisaprices")} />
      {/* <DropdownItem to="/requests" icon="ri-file-list-3-line" label="Requests" active={is("visarequests")} /> */}

    </NavDropdown>


    {/* <NavDropdown
      icon="ri-luggage-deposit-line"
      label="Packages"
      active={anyOf("mypackages","DMCPACKAGECREATE", "DMCPACKAGESLIST", "DMCPACKAGEENQUIRYLIST")}

    >
       <DropdownItem to="/mypackages" icon="ri-add-circle-line" label="My Packages" active={is("mypackages")} />
      <DropdownItem to="/dmcpackagecreate" icon="ri-add-circle-line" label="Create Package" active={is("DMCPACKAGECREATE")} />
      <DropdownItem to="/dmcpackageslist" icon="ri-list-check-2" label="All Packages" active={is("DMCPACKAGESLIST")} />
      <DropdownItem to="/dmcpackageenquirylist" icon="ri-questionnaire-line" label="Enquiries" active={is("DMCPACKAGEENQUIRYLIST")} />
    </NavDropdown> */}

    <NavDropdown
      icon="ri-luggage-deposit-line"
      label="Packages"
      active={anyOf("createpackage", "mypackages", "masterpackages", "customerpackages", "packageenquiries")}
    >
      <DropdownItem to="/createpackage" icon="ri-add-circle-line" label="Create Package" active={is("createpackage")} />
      <DropdownItem to="/companypackages" icon="ri-robot-line" label="My Packages " active={is("mypackages")} />
      {/* <DropdownItem to="/masterpackages" icon="ri-suitcase-line" label="Global Packages" active={is("masterpackages")} /> */}
      <DropdownItem to="/customerpackages" icon="ri-user-3-line" label="Customer Packages" active={is("customerpackages")} />
      <DropdownItem to="/dmcpackageenquirylist" icon="ri-question-line" label="Enquiries" active={is("packageenquiries")} />
    </NavDropdown>



    <NavDropdown
      icon="ri-car-line"
      label="Cabs"
      active={anyOf("vehicles", "vehiclepricing", "vehicledriver", "dmcbookings")}
    >
      <DropdownItem to="/dmc/vehicles" icon="ri-car-line" label="Vehicles" active={is("vehicles")} />
      <DropdownItem to="/dmc/vehicle-pricing" icon="ri-price-tag-3-line" label="Vehicle Pricing" active={is("vehiclepricing")} />
      <DropdownItem to="/dmc-vehicle-driver" icon="ri-user-star-line" label="Vehicle Drivers" active={is("vehicledriver")} />
      <DropdownItem to="/dmc-transfer/bookings" icon="ri-book-2-line" label="Bookings" active={is("dmcbookings")} />
    </NavDropdown>


    {showMenu("accounts") && (
      <NavDropdown
        icon="ri-money-dollar-circle-line"
        label="Accounts"
        active={anyOf("accountsdashboard", "salelist", "purchaselist", "expenselist", "cheques", "profitlossdetailedrepor", "supplierledger", "agentledger", "companybankaccounts")}
      >
        <DropdownItem to="/accounts-dashboard" icon="ri-dashboard-line" label="Dashboard" active={is("accountsdashboard")} />
        <DropdownItem to="/salelist" icon="ri-shopping-cart-line" label="Sales" active={is("salelist")} />
        <DropdownItem to="/purchaselist" icon="ri-shopping-bag-line" label="Purchases" active={is("purchaselist")} />
        <DropdownItem to="/expenselist" icon="ri-money-dollar-circle-line" label="Expenses" active={is("expenselist")} />
        <DropdownItem to="/cheques" icon="ri-bank-card-line" label="Cheques" active={is("cheques")} />
        <DropdownItem to="/profit-loss-detailedreport" icon="ri-line-chart-line" label="Profit & Loss" active={is("profitlossdetailedrepor")} />
        <DropdownItem to="/supplier-ledger" icon="ri-store-2-line" label="Supplier Ledger" active={is("supplierledger")} />
        <DropdownItem to="/agent-ledger" icon="ri-user-star-line" label="Agent Ledger" active={is("agentledger")} />
        <DropdownItem to="/companybankaccounts" icon="ri-bank-line" label="Bank Accounts" active={is("companybankaccounts")} />
      </NavDropdown>
    )}
  </>
);

const SupplierNav = ({ is }) => (
  <>
    <NavLink to="/holidaydashboard" icon="ri-home-4-line" label="Holidays" active={is("holidays")} />
    <NavLink to="/visadashboard" icon="ri-passport-line" label="Visas" active={is("visas")} />
    <NavLink to="/leads" icon="ri-message-3-line" label="Leads" active={is("leads")} />
    <NavLink to="/seriesfare/dashboard" icon="ri-flight-takeoff-line" label="Series Fares" active={is("seriesfares")} />
    <NavLink to="/accounts-dashboard" icon="ri-money-dollar-circle-line" label="Accounts" active={is("accounts")} />
  </>
);

const VisaNav = ({ is }) => (
  <>
    <NavLink to="/visadashboard" icon="ri-passport-line" label="Dashboard" active={is("VISADASHBOARD")} />
    <NavLink to="/supplier/visaprices" icon="ri-money-dollar-circle-line" label="Prices" active={is("PRICES")} />
    <NavLink to="/supplier/visarequests" icon="ri-message-3-line" label="Requests" active={is("REQUESTS")} />
    <NavLink to="/supplier/visaappointments" icon="ri-calendar-check-line" label="Appointments" active={is("APPOINTMENTS")} />
    <NavLink to="/leads" icon="ri-message-3-line" label="Leads" active={is("leads")} />
    <NavLink to="/accounts-dashboard" icon="ri-money-dollar-circle-line" label="Accounts" active={is("accounts")} />
  </>
);

const SeriesFairNav = ({ is }) => (
  <>
    <NavLink to="/seriesfare/dashboard" icon="ri-home-4-line" label="Dashboard" active={is("SERIESFAREDASHBOARD")} />
    <NavLink to="/seriesfare" icon="ri-add-circle-line" label="Create Series Fare" active={is("SERIESFARECREATE")} />
    <NavLink to="/seriesfarelist" icon="ri-list-check-2" label="Lists" active={is("SERIESFARELISTS")} />
    <NavLink to="/seriesfare/blockrequest" icon="ri-message-3-line" label="Requests" active={is("SERIESFAREREQUESTS")} />
    <NavLink to="/leads" icon="ri-message-3-line" label="Leads" active={is("leads")} />
    <NavLink to="/accounts-dashboard" icon="ri-money-dollar-circle-line" label="Accounts" active={is("accounts")} />
  </>
);

// ─── Header ───────────────────────────────────────────────────────────────────
export default function Header({ activeSection }) {
  const { user, loading } = useAuth();
  const planIsExpired = user ? isPlanExpired(user.endDate) : false;

  if (loading || !user) {
    return (
      <div
        className="main-header"
        style={{ minHeight: "60px", backgroundColor: "#FFFFFF", borderBottom: "1px solid #DEE2E6" }}
      />
    );
  }

  const is = (s) => activeSection === s;
  const anyOf = (...ss) => ss.some((s) => activeSection === s);

  const specialCompanyTypes = ["VISA", "SERIESFAIR", "DMC", "HOTEL", "SUPPLIER"];
  const isSpecial = specialCompanyTypes.includes(user.companyType);

  return (
    <div
      className="main-header"
      style={{
        backgroundColor: BG,
        borderBottom: "1px solid #DEE2E6",
        padding: "0.4rem 0.5rem",
        position: "sticky",
        top: "70px",
        zIndex: 1020,
        minHeight: "60px",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div className="container-fluid">
        {planIsExpired ? (
          <div className="ticker-wrap">
            <div className="ticker-move fw-bold text-danger">
              To access our full suite of services... please subscribe to a plan.
            </div>
          </div>
        ) : (
          <nav className="d-flex align-items-center gap-1 flex-wrap">
            {isSpecial ? (
              <>
                {user.companyType === "HOTEL" && showMenu("hotels") && <HotelNav is={is} />}
                {user.companyType === "DMC" && showMenu("packages") && <DmcNav is={is} anyOf={anyOf} user={user} />}
                {user.companyType === "SUPPLIER" && <SupplierNav is={is} />}
                {user.companyType === "VISA" && showMenu("visas") && <VisaNav is={is} />}
                {user.companyType === "SERIESFAIR" && showMenu("flights") && <SeriesFairNav is={is} />}
              </>
            ) : (
              <StandardNav is={is} anyOf={anyOf} user={user} />
            )}
          </nav>
        )}
      </div>
    </div>
  );
}


